import {
  StyleSheet,
  TextInput,
  Image,
  Platform,
  BackHandler,
  View,
  Pressable,
  Keyboard,
  TouchableOpacity,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Button from '../../components/Reusables/Button';
import Typography from '../../components/Reusables/Typography';
import { AuthContext } from '../../Context/AuthContext';
import PhoneInput, {
  ICountry,
  IPhoneInputRef,
} from 'rn-international-phone-number';
import AntDesign from '@react-native-vector-icons/ant-design/static';
import Entypo from '@react-native-vector-icons/entypo/static';
import LinearGradient from 'react-native-linear-gradient';
import ErrorContext from '../../Context/ErrorContext';
import Carousel from 'react-native-reanimated-carousel';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import { getEmployeeProfileDetails } from '../../api/employees';
import {
  addInAppEmployeeInfo,
  clearEmployeeAndBusinessInfo,
} from '../../redux/AuthSlice';
import {
  markOwnerSetupSessionReady,
  resetOwnerActivationForIdentity,
} from '../../redux/ownerActivationSlice';
import { clearOwnerSetupHandoffMarker } from '../../services/ownerSetupSessionHandoff';
import { addInAppBusinessInfo } from '../../redux/AuthSlice';
import { useDispatch } from 'react-redux';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { fs, v, dumpTokens, s, iconSize, borderWidth } from '../../utils/libs';
import store, { logoutAction } from '../../redux/Store';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import { buildClientAnalytics } from '../../utils/ClientAnalytics';
import { postUserAnalytics } from '../../api/analytics';
import { theme, GTFonts, useLayoutSize } from '../../theme';
import { isTablet } from 'react-native-device-info';
import { useFocusEffect } from '@react-navigation/native';
import { PREFERRED_COUNTRIES } from '../../utils/phone';
import { useDetectedCountry } from '../../hooks/useDetectedCountry';
import { shouldUseWebLayout } from '../../utils/layoutUtils';
import { useResetPasscode } from '../../Context/ResetPasscodeContext';
import { setEmployeeEmail } from '../../services/SecureCredentials';
import { isEmployeeOwnerRole } from '../../utils/roles';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import { axiosBackoffice } from '../../axiosClient';
import {
  preloadEmployeeSession,
  preloadOwnerSession,
} from '../../services/postLoginPreload';
import type { AppDispatch } from '../../redux/Store';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import { buildAppAnalyticsIdentify } from '../../services/appAnalyticsIdentify';
const url = 'https://www.bizpluspos.co.ke';

const classifyLoginErrorCategory = (
  message?: string,
  kind?: 'offline' | 'validation',
): 'network' | 'validation' | 'authentication' | 'unknown' => {
  if (kind === 'offline') return 'network';
  if (kind === 'validation') return 'validation';
  const m = String(message || '').toLowerCase();
  if (m.includes('internet') || m.includes('network') || m.includes('connect'))
    return 'network';
  if (
    m.includes('password') ||
    m.includes('credential') ||
    m.includes('invalid') ||
    m.includes('incorrect') ||
    m.includes('unauthorized') ||
    m.includes('not found')
  )
    return 'authentication';
  if (m.includes('something went wrong')) return 'unknown';
  return 'authentication';
};

const Login = ({ navigation }: { navigation: any }) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isLargeScreen = windowWidth >= 768;
  const useWebLikeLayout = shouldUseWebLayout(windowWidth, windowHeight);
  const scrollViewRef = useRef<ScrollView>(null);
  const { autoLoginOwner } = useBackofficeOwnerAuth();
  const axiosprivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const {
    handleLogin,
    errorMessage = '',
    isLoading,
    tokenAssistant,
    setAuth,
    setIsLoading,
  } = useContext(AuthContext);
  const { handleError } = useContext(ErrorContext);
  const { clearSession: clearResetPasscodeSession } = useResetPasscode();
  const [phoneNumber, setPhoneNumber] = useState('');
  // international phone input: keep calling code separate from national number
  const [phoneInputValue, setPhoneInputValue] = useState<string>('');
  const [phoneSelectedCountry, setPhoneSelectedCountry] = useState<
    ICountry | undefined
  >(undefined);
  const phoneInputRef = React.useRef<IPhoneInputRef | null>(null);
  const [validPhone, setValidPhone] = useState(false);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [password, setPassword] = useState('');
  const [showpassword, setShowPassword] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [focusedInput, setFocusedInput] = useState<'phone' | 'password' | null>(
    null,
  );
  const { width, height } = useLayoutSize();
  const { countryCode, iCountry } = useDetectedCountry();

  const buildE164From = (
    input: string,
    countryOverride?: ICountry | undefined,
  ): string => {
    const digitsOnly = String(input || '').replace(/[^0-9]/g, '');
    const country = countryOverride || phoneInputRef.current?.getCountry?.();
    const root = (country as any)?.idd?.root as string | undefined;
    const suffixes = (((country as any)?.idd?.suffixes as string[]) ||
      []) as string[];
    let code = '';
    if (root) {
      code = `${root}${suffixes?.[0] ?? ''}`.replace(/\s+/g, '');
      if (!code.startsWith('+')) code = `+${code}`;
    }
    if (code && digitsOnly) return `${code}${digitsOnly}`;
    if (digitsOnly) return `+${digitsOnly}`;
    return '';
  };

  const imageData = useMemo(
    () => [
      shouldUseWebLayout(windowWidth, windowHeight) || isTablet()
        ? require('../../assets/login/image3.webp')
        : require('../../assets/login/image2.webp'),
    ],
    [windowWidth, windowHeight],
  );
  useEffect(() => {
    errorMessage &&
      errorMessage?.length > 1 &&
      handleError(errorMessage, 'error', true);
  }, [errorMessage]);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({ y: 0, animated: false });
      }
    }, []),
  );

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== 'android') return;
      const sub = BackHandler.addEventListener('hardwareBackPress', () => true);
      return () => sub.remove();
    }, []),
  );

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(
      'keyboardDidShow',
      () => {
        console.log('Keyboard shown');
        setIsKeyboardVisible(true);
      },
    );
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      () => {
        console.log('Keyboard hidden');
        scrollViewRef.current &&
          scrollViewRef.current.scrollTo({ y: 0, animated: false });
        setIsKeyboardVisible(false);
      },
    );

    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
      setIsLoading(false);
    };
  }, []);

  // Initialize selected country from detected SIM/IP
  useEffect(() => {
    if (!phoneSelectedCountry && iCountry) {
      setPhoneSelectedCountry(iCountry);
    }
  }, [phoneSelectedCountry, iCountry]);

  // Validate phone using library validation (same pattern as Register)
  useEffect(() => {
    setValidPhone(!!phoneInputRef.current?.isValidPhoneNumber);
  }, [phoneNumber]);

  const navigateToRegister = () => {
    const ctx = buildAppAnalyticsContext();
    appAnalytics.track('acq_create_account_tapped', {
      ...ctx,
      source: 'login',
    });
    navigation.navigate('Register');
  };

  const navigateToResetPasscode = () => {
    clearResetPasscodeSession();
    navigation.navigate('ResetPasscodeRequest');
  };

  const handleSubmit = async () => {
    // Validate phone before any network or auth logic
    const isPhoneValid = !!phoneInputRef.current?.isValidPhoneNumber;
    if (!isPhoneValid || !phoneNumber) {
      const ctx = buildAppAnalyticsContext();
      appAnalytics.track('auth_login_failed', {
        ...ctx,
        error_category: classifyLoginErrorCategory(undefined, 'validation'),
      });
      handleError('Please enter a valid phone number', 'error', true);
      return;
    }
    // Strong connectivity check before any auth logic
    const netState = await NetInfo.fetch();
    const isOnline = !!(
      netState.isConnected &&
      (netState.isInternetReachable ?? false)
    );
    if (!isOnline) {
      const ctx = buildAppAnalyticsContext();
      appAnalytics.track('auth_login_failed', {
        ...ctx,
        error_category: classifyLoginErrorCategory(undefined, 'offline'),
      });
      handleError(
        'No internet connection. Please check your data or wifi and try again.',
        'error',
        true,
      );
      return;
    }
    await preLoginCleanUp();
    const result = await handleLogin(phoneNumber, password);
    console.log('result', result);
    if (
      result.status === 'success' &&
      result.accessToken &&
      result.refreshToken
    ) {
      // setTimeout(() => {
      //   getEmployeeInfo(result.accessToken!, result.refreshToken!);
      // }, 32);
      getEmployeeInfo(result.accessToken, result.refreshToken);
    } else if (result.status === 'verification_required') {
      navigation.navigate('Verification', { phone: phoneNumber });
    } else if (result.status === 'error') {
      const ctx = buildAppAnalyticsContext();
      appAnalytics.track('auth_login_failed', {
        ...ctx,
        error_category: classifyLoginErrorCategory(result.message),
      });
      handleError(result.message || 'Something went wrong', 'error', true);
    }
  };

  const preLoginCleanUp = async () => {
    dumpTokens();
    setAuth({ accessToken: '', refreshToken: '' });
    await AsyncStorage.removeItem('refreshToken');
    await AsyncStorage.removeItem('accessToken');
    dispatch(clearEmployeeAndBusinessInfo());
    dispatch(logoutAction());
  };

  const getEmployeeInfo = async (accessToken: string, refreshToken: string) => {
    try {
      const response = await getEmployeeProfileDetails(axiosprivate, {
        accessToken,
      });
      console.log('getEmployeeProfileDetails', response);
      if (response.data.status === 200) {
        if (response?.data.data.bp_employee_requires_reauthentication) {
          handleError(
            'Your session has expired. You will be be prompted to login again with the new credentials.',
            'error',
            true,
          );
          setTimeout(() => {
            clearAll();
          }, 4000);
          return;
        }
        if (response.data.message === 'Employee does not exist') {
          clearAll();
          return;
        }

        setAuth({ accessToken, refreshToken });
        const data = response?.data.data;
        console.log('getEmployeeInfo', response);

        dispatch(addInAppEmployeeInfo(data));
        console.log(response);
        dispatch(
          addInAppEmployeeInfo({
            ...data,
            bp_owner_phone_number: response?.data?.ownerPhoneNumber,
          }),
        );

        // Owner-setup identity reconciliation: pre-login cleanup preserved
        // any owner-bound activation envelope; now that the authenticated
        // owner identity is known, resume it on match or reset on mismatch.
        const authenticatedOwnerKey = String(data?.bp_owner_id || '') || null;
        dispatch(
          resetOwnerActivationForIdentity({ ownerKey: authenticatedOwnerKey }),
        );
        // On a matching owner, a successful login resolves any pending /
        // requires_login session bootstrap; the handoff marker is no longer
        // needed once the session is ready.
        dispatch(markOwnerSetupSessionReady());
        void clearOwnerSetupHandoffMarker();
        dispatch(
          addInAppBusinessInfo({
            name: response?.data.businessInformation.bp_user_business_name,
            description:
              response?.data?.businessInformation?.bp_user_business_description,
            Adrress: response?.data.addressInformation,
            type: response?.data?.businessInformation?.bp_user_business_type,
          }),
        );

        let locationAfterPreload: { currentLocationId: string | null } = {
          currentLocationId: null,
        };
        try {
          locationAfterPreload = await preloadEmployeeSession({
            dispatch: dispatch as AppDispatch,
            axiosPrivate: axiosprivate,
            accessToken,
            employeeId: String(data?._id || ''),
          });

          const email = String(data?.bp_employee_email_address || '').trim();
          if (email) {
            await setEmployeeEmail(email);
          }

          const role = String(data?.bp_employee_role || '');
          if (isEmployeeOwnerRole(role)) {
            const ownerResult = await autoLoginOwner();
            if (
              ownerResult &&
              'accessToken' in ownerResult &&
              ownerResult.accessToken
            ) {
              await preloadOwnerSession({
                dispatch: dispatch as AppDispatch,
                axiosBackoffice,
                ownerAccessToken: ownerResult.accessToken,
                ownerId: String(data?.bp_owner_id || ''),
                currentLocationId: locationAfterPreload.currentLocationId,
              });
            }
          }
        } catch (e) {
          console.log('post-login preload skipped', e);
        }

        // Fire-and-forget user analytics (non-blocking)
        try {
          const analyticsPayload = await buildClientAnalytics({
            userId: String(data?._id ?? ''),
            employeeId: String(data?._id ?? ''),
            ownerId: String(response?.data?.data?.bp_owner_id ?? ''),
            phoneNumber: String(response?.data?.ownerPhoneNumber ?? ''),
            lastLoginAt: new Date(),
          });
          postUserAnalytics(analyticsPayload, { accessToken }).then(() => {});
        } catch (e) {
          console.log('analytics submit skipped', e);
        }

        try {
          const location = store.getState().location.currentLocation;
          const business = {
            name: response?.data?.businessInformation?.bp_user_business_name,
          };
          const employee = {
            ...data,
            bp_owner_phone_number: response?.data?.ownerPhoneNumber,
          };
          const locationType =
            location?.bp_location_is_system_generated === true
              ? 'test'
              : location
                ? 'real'
                : undefined;
          const ctx = buildAppAnalyticsContext({
            role: employee?.bp_employee_role,
            locationType,
          });
          appAnalytics.track('auth_login_succeeded', { ...ctx });
          const identify = buildAppAnalyticsIdentify({
            employee,
            location: location
              ? {
                  ...location,
                  bp_location_type: locationType ?? location.bp_location_type,
                }
              : location,
            business,
          });
          if (identify) appAnalytics.identify(identify);
        } catch (e) {
          console.log('app analytics login skipped', e);
        }
      } else if (response.data.message === 'Employee does not exist') {
        clearAll();
        return;
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    } finally {
    }
  };

  const clearAll = async () => {
    dumpTokens();
    setAuth({ accessToken: '', refreshToken: '' });
    dispatch(clearEmployeeAndBusinessInfo());
    dispatch(logoutAction());
    setIsLoading(false);
  };

  // Web layout with side-by-side design
  // Viewport-locked shell: hero stays pinned; form column never resizes the image.
  if (useWebLikeLayout && isLargeScreen) {
    const styles = createStyles(theme as any, width, height);
    return (
      <View
        style={[
          styles.webContainer,
          { height: windowHeight, maxHeight: windowHeight },
        ]}>
        {/* Left side - Login Form (no nested card — Stripe/Linear pattern) */}
        <View style={styles.webRightPanel}>
          <View style={styles.webFormCard}>
            <View style={styles.webFormHeader}>
              <Typography style={styles.webBrandMark}>BizPlus POS</Typography>
              <Typography style={styles.webLoginTitle}>Welcome back</Typography>
              <Typography style={styles.webLoginSubtitle}>
                Sign in to continue to your account
              </Typography>
            </View>

            <View style={styles.webFormContainer}>
              {/* Phone number input */}
              <View style={styles.webFormGroup}>
                <Typography style={styles.webLabel}>Phone Number</Typography>
                <PhoneInput
                  phoneInputStyles={{
                    container: {
                      height: 52,
                      backgroundColor: theme.colors.surface,
                      borderWidth: 1,
                      borderColor: theme.colors.borderGray200,
                      borderRadius: 10,
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                    },
                    input: {
                      fontFamily: GTFonts.semibold,
                      fontSize: fs(16),
                      lineHeight: fs(20),
                      color: theme.colors.textGray700,
                      outlineStyle: 'none',
                    } as any,
                    callingCode: {
                      fontFamily: GTFonts.semibold,
                      fontSize: fs(16),
                      color: theme.colors.textGray700,
                      lineHeight: fs(20),
                    },
                  }}
                  ref={phoneInputRef}
                  defaultCountry={countryCode as any}
                  value={phoneInputValue}
                  modalType="bottomSheet"
                  placeholderTextColor={theme.colors.textSecondary}
                  placeholder="720 000 000"
                  popularCountries={PREFERRED_COUNTRIES}
                  modalPopularCountriesTitle="Popular countries"
                  modalAllCountriesTitle="All countries"
                  minBottomsheetHeight={'50%'}
                  initialBottomsheetHeight={'80%'}
                  maxBottomsheetHeight={'92%'}
                  onFocus={() => {
                    setPhoneFocused(true);
                    setFocusedInput('phone');
                  }}
                  onBlur={() => {
                    setPhoneFocused(false);
                    setFocusedInput(null);
                  }}
                  onChangePhoneNumber={v => {
                    const startsWithZero = v.startsWith('0');
                    if (startsWithZero) {
                      v = v.slice(1);
                    }
                    setPhoneInputValue(v);
                    const full = buildE164From(v, phoneSelectedCountry);
                    setPhoneNumber(full);
                  }}
                  country={phoneSelectedCountry}
                  onChangeCountry={c => {
                    setPhoneSelectedCountry(c);
                    const full = buildE164From(phoneInputValue, c);
                    setPhoneNumber(full);
                  }}
                />
              </View>

              {/* Password input */}
              <View style={styles.webFormGroup}>
                <View style={styles.webLabelRow}>
                  <Typography style={styles.webLabelInline}>
                    Password / Pin
                  </Typography>
                  <TouchableOpacity
                    onPress={navigateToResetPasscode}
                    hitSlop={8}>
                    <Typography style={styles.webForgotInlineText}>
                      Forgot passcode?
                    </Typography>
                  </TouchableOpacity>
                </View>
                <View style={styles.webInputWrapper}>
                  <TextInput
                    style={styles.webInput}
                    secureTextEntry={!showpassword}
                    autoCorrect={false}
                    placeholderTextColor={theme.colors.textSecondary}
                    placeholder="Enter your password"
                    value={password}
                    onChangeText={setPassword}
                    // Web: treat Enter as clicking "Log In"
                    onKeyPress={(e: any) => {
                      const key = e?.nativeEvent?.key ?? e?.key;
                      if (key !== 'Enter') return;
                      e?.preventDefault?.();
                      e?.stopPropagation?.();
                      if (isLoading || !validPhone || password.length <= 3)
                        return;
                      void handleSubmit();
                    }}
                    // Native: press "done"/"enter" to submit
                    returnKeyType="done"
                    onSubmitEditing={() => {
                      if (isLoading || !validPhone || password.length <= 3)
                        return;
                      void handleSubmit();
                    }}
                    onFocus={() => setFocusedInput('password')}
                    onBlur={() => setFocusedInput(null)}
                  />
                  <Pressable
                    style={styles.webEyeButton}
                    hitSlop={12}
                    onPress={() => setShowPassword(!showpassword)}>
                    <Entypo
                      name={showpassword ? 'eye' : 'eye-with-line'}
                      size={20}
                      color={theme.colors.textGray500}
                    />
                  </Pressable>
                </View>
              </View>

              {/* Login Button */}
              <TouchableOpacity
                style={[
                  styles.webLoginButton,
                  (isLoading || !validPhone || password.length <= 3) &&
                    styles.webLoginButtonDisabled,
                ]}
                onPress={handleSubmit}
                disabled={isLoading || !validPhone || password.length <= 3}>
                {isLoading ? (
                  <Typography style={styles.webLoginButtonText}>
                    Signing in...
                  </Typography>
                ) : (
                  <View style={styles.webButtonContent}>
                    <Typography style={styles.webLoginButtonText}>
                      Log In
                    </Typography>
                    <Entypo name="lock" size={18} color={theme.colors.textInverse} />
                  </View>
                )}
              </TouchableOpacity>

              {/* Create Account Link */}
              <View style={styles.webSignupLink}>
                <Typography style={styles.webSignupText}>
                  Don't have an account?{' '}
                </Typography>
                <TouchableOpacity onPress={navigateToRegister}>
                  <Typography style={styles.webSignupLinkText}>
                    Create Account
                  </Typography>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>

        {/* Right side - Branding */}
        <View style={styles.webLeftPanel}>
          <View style={styles.webBrandingContent}>
            <Image
              style={styles.webHeroImage}
              source={imageData[0]}
              resizeMode="cover"
            />
            <LinearGradient
              colors={[
                'rgba(15,23,42,0.50)',
                'rgba(15,23,42,0.24)',
                'rgba(15,23,42,0.48)',
              ]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.webOverlay}>
              <Typography style={styles.webBrandTitle}>BizPlus POS</Typography>
              <Typography style={styles.webBrandSubtitle}>
                Join thousands of businesses simplifying their operations with
                BizPlus POS solution
              </Typography>
              <View style={styles.webFeatureList}>
                <View style={styles.webFeatureItem}>
                  <AntDesign name="check-circle" size={16} color={theme.colors.successLight} />
                  <Typography style={styles.webFeatureText}>
                    Smart Inventory Management
                  </Typography>
                </View>
                <View style={styles.webFeatureItem}>
                  <AntDesign name="check-circle" size={16} color={theme.colors.successLight} />
                  <Typography style={styles.webFeatureText}>
                    CashFlow Management
                  </Typography>
                </View>
                <View style={styles.webFeatureItem}>
                  <AntDesign name="check-circle" size={16} color={theme.colors.successLight} />
                  <Typography style={styles.webFeatureText}>
                    M-Pesa Integration
                  </Typography>
                </View>
              </View>
            </LinearGradient>
          </View>
        </View>
      </View>
    );
  }

  // Mobile layout (original)
  const styles = createStyles(theme as any, width, height);
  return (
    <View style={styles.safeArea}>
      <KeyboardAwareFormScroll
        style={styles.scrollViewContent}
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps="handled"
        ref={scrollViewRef}
        // baseBottomPadding={v(100)}
        bottomOffset={v(200)}>
        <View style={styles.maincontainer}>
          <View style={styles.top}>
            {
              <View style={styles.carouselContainer}>
                <Carousel
                  loop={false}
                  width={width}
                  height={height * (isTablet() ? 0.4 : 0.3)}
                  autoPlay={true}
                  autoPlayInterval={8000}
                  data={imageData}
                  mode="parallax"
                  modeConfig={{
                    parallaxScrollingScale: 1,
                    parallaxScrollingOffset: 10,
                    parallaxAdjacentItemScale: 0.7,
                  }}
                  scrollAnimationDuration={1000}
                  onSnapToItem={(index: number) =>
                    console.log('current index:', index)
                  }
                  // containerStyle={{ backgroundColor: 'rgba(30, 64, 175, 0.25)' }}
                  renderItem={({
                    item,
                    index,
                  }: {
                    item: any;
                    index: number;
                  }) => (
                    <View key={index} style={styles.carouselItemContainer}>
                      <Image style={styles.carouselImage} source={item} />
                    </View>
                  )}
                />
              </View>
            }
          </View>
          <View style={styles.logincontainer}>
            <View style={styles.loginContent}>
              <Typography style={styles.loginTitle}>
                Enter Login Credentials
              </Typography>
              <View style={styles.formContainer}>
                {/* Phone number input */}
                <View style={styles.form}>
                  <Typography style={styles.text}>Phone Number</Typography>
                  <PhoneInput
                    phoneInputStyles={{
                      container: {
                        height: v(50),
                        backgroundColor: theme.colors.surface,
                        borderWidth: borderWidth(
                          Platform.OS === 'web' ? 0.5 : 1,
                        ),
                        width: '100%',
                      },
                      input: {
                        fontFamily: GTFonts.semibold,
                        fontSize: fs(16),
                        lineHeight: fs(18),
                        ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
                      } as any,
                      callingCode: {
                        fontFamily: GTFonts.semibold,
                        fontSize: fs(16),
                        lineHeight: fs(18),
                      },
                    }}
                    ref={phoneInputRef}
                    defaultCountry={countryCode as any}
                    value={phoneInputValue}
                    modalType="bottomSheet"
                    placeholder="720 000 000"
                    popularCountries={PREFERRED_COUNTRIES}
                    modalPopularCountriesTitle="Popular countries"
                    modalAllCountriesTitle="All countries"
                    minBottomsheetHeight={'50%'}
                    initialBottomsheetHeight={'80%'}
                    maxBottomsheetHeight={'92%'}
                    onFocus={() => {
                      setPhoneFocused(true);
                      setFocusedInput('phone');
                    }}
                    onBlur={() => {
                      setPhoneFocused(false);
                      setFocusedInput(null);
                    }}
                    onChangePhoneNumber={v => {
                      const startsWithZero = v.startsWith('0');

                      if (startsWithZero) {
                        v = v.slice(1);
                      }

                      setPhoneInputValue(v);
                      const full = buildE164From(v, phoneSelectedCountry);
                      console.log('full', full);
                      setPhoneNumber(full);
                    }}
                    country={phoneSelectedCountry}
                    onChangeCountry={c => {
                      setPhoneSelectedCountry(c);
                      const full = buildE164From(phoneInputValue, c);
                      console.log('full', full);
                      setPhoneNumber(full);
                    }}
                  />
                </View>

                {/* Password input */}
                <View style={styles.form}>
                  <Typography style={styles.text}>Password/Pin</Typography>
                  <View style={styles.inputWrapper}>
                    <TextInput
                      style={styles.input}
                      secureTextEntry={showpassword ? false : true}
                      autoCorrect={false}
                      placeholderTextColor={theme.colors.textTertiary}
                      placeholder="*********"
                      value={password}
                      onChangeText={(e: string) => setPassword(e)}
                      returnKeyType="done"
                      onSubmitEditing={() => {
                        if (isLoading || !validPhone || password.length <= 3)
                          return;
                        void handleSubmit();
                      }}
                      onFocus={() => setFocusedInput('password')}
                      onBlur={() => setFocusedInput(null)}
                    />
                    <Pressable
                      style={styles.eyes}
                      onPress={() => setShowPassword(!showpassword)}>
                      {showpassword ? (
                        <Entypo
                          name="eye"
                          size={iconSize(20)}
                          color={theme.colors.borderGray}
                        />
                      ) : (
                        <Entypo
                          name="eye-with-line"
                          size={iconSize(20)}
                          color={theme.colors.borderGray}
                        />
                      )}
                    </Pressable>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
        <View style={styles.loginLinkContainer}>
          <Typography style={styles.loginText}>
            Don't have an account?{' '}
          </Typography>
          <TouchableOpacity hitSlop={10} onPress={navigateToRegister}>
            <Typography style={styles.loginLink}>Create Account</Typography>
          </TouchableOpacity>
        </View>
        <View style={styles.forgotPasscodeRow}>
          <TouchableOpacity hitSlop={10} onPress={navigateToResetPasscode}>
            <Typography style={styles.forgotPasscodeLink}>
              Forgot passcode?
            </Typography>
          </TouchableOpacity>
        </View>
      </KeyboardAwareFormScroll>
      <KeyboardStickyView offset={{ closed: 0, opened: v(30) }}>
        <View style={styles.stickyButtonContainer}>
          <Button
            backgroundColor={theme.colors.primary}
            text={isLoading ? 'Logging in' : 'Login'}
            loading={isLoading}
            disabled={isLoading || !validPhone || password.length <= 3}
            icon={'lock'}
            borderRadius={s(8)}
            height={v(46)}
            rippleColor="5d91c2"
            onPress={handleSubmit}
          />
        </View>
      </KeyboardStickyView>
    </View>
  );
};

export default Login;

const createStyles = (theme: any, width: number, height: number) => {
  return StyleSheet.create({
    webContainer: {
      flexDirection: 'row',
      backgroundColor: theme.colors.textGray900,
      width: '100%',
      overflow: 'hidden',
    },
    webLeftPanel: {
      flex: 3,
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
    },
    webBrandingContent: {
      ...StyleSheet.absoluteFillObject,
    },
    webHeroImage: {
      ...StyleSheet.absoluteFillObject,
      width: '100%',
      height: '100%',
    },
    webOverlay: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
      padding: s(48),
    },
    webBrandTitle: {
      fontSize: fs(48),
      lineHeight: fs(56),
      fontWeight: '700',
      color: theme.colors.primaryDark,
      fontFamily: GTFonts.semibold,
      marginBottom: v(16),
      textAlign: 'center',
      textShadowColor: 'rgba(0,0,0,0.35)',
      textShadowOffset: { width: 0, height: 2 },
      textShadowRadius: 8,
    },
    webBrandSubtitle: {
      fontSize: fs(18),
      color: theme.colors.textInverse,
      fontFamily: GTFonts.semibold,
      textAlign: 'center',
      maxWidth: s(400),
      lineHeight: fs(28),
      marginBottom: v(32),
      textShadowColor: 'rgba(0,0,0,0.55)',
      textShadowOffset: { width: 0, height: 2 },
      textShadowRadius: 6,
    },
    webFeatureList: {
      gap: v(12),
    },
    webFeatureItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
    },
    webFeatureText: {
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.textInverse,
      fontFamily: GTFonts.semibold,
      textShadowColor: 'rgba(0,0,0,0.45)',
      textShadowOffset: { width: 0, height: 1 },
      textShadowRadius: 4,
    },
    webRightPanel: {
      flex: 2,
      height: '100%',
      backgroundColor: theme.colors.surface,
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      paddingHorizontal: s(48),
      paddingVertical: v(40),
    },
    webFormCard: {
      width: '100%',
      maxWidth: s(400),
      backgroundColor: 'transparent',
    },
    webFormHeader: {
      marginBottom: v(36),
    },
    webBrandMark: {
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.primary,
      fontFamily: GTFonts.semibold,
      marginBottom: v(20),
    },
    webWelcomeText: {
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.primary,
      fontFamily: GTFonts.semibold,
      marginBottom: v(8),
    },
    webLoginTitle: {
      fontSize: fs(30),
      lineHeight: fs(36),
      color: theme.colors.textGray900,
      fontFamily: GTFonts.semibold,
      marginBottom: v(8),
    },
    webLoginSubtitle: {
      fontSize: fs(15),
      lineHeight: fs(22),
      color: theme.colors.textGray500,
      fontFamily: GTFonts.regular,
    },
    webFormContainer: {
      gap: v(20),
    },
    webFormGroup: {
      marginBottom: v(4),
    },
    webLabel: {
      fontSize: fs(14),
      lineHeight: fs(20),
      fontWeight: '600',
      color: theme.colors.textGray700,
      fontFamily: GTFonts.semibold,
      marginBottom: v(8),
    },
    webLabelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: v(8),
    },
    webLabelInline: {
      fontSize: fs(14),
      lineHeight: fs(20),
      fontWeight: '600',
      color: theme.colors.textGray700,
      fontFamily: GTFonts.semibold,
    },
    webForgotInlineText: {
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.primary,
      fontFamily: GTFonts.semibold,
    },
    webInputWrapper: {
      backgroundColor: theme.colors.surface,
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray200,
      borderRadius: s(10),
      height: v(52),
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: s(16),
    },
    webInput: {
      flex: 1,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.textGray700,
      fontFamily: GTFonts.regular,
      height: '100%',
      outlineStyle: 'none',
    } as any,
    webEyeButton: {
      padding: s(8),
      marginLeft: s(8),
    },
    webLoginButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: s(10),
      height: v(52),
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: v(8),
      cursor: 'pointer',
    } as any,
    webLoginButtonDisabled: {
      // Match native <Button disabled /> background
      backgroundColor: theme.colors.borderGray,
      cursor: 'not-allowed',
    } as any,
    webButtonContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
    },
    webLoginButtonText: {
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.textInverse,
      fontFamily: GTFonts.semibold,
    },
    webSignupLink: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: v(8),
      paddingTop: v(20),
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.borderGray200,
    },
    webSignupText: {
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.textGray500,
      fontFamily: GTFonts.regular,
    },
    webSignupLinkText: {
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.primary,
      fontWeight: '600',
      fontFamily: GTFonts.semibold,
    },
    webForgotLink: {
      marginTop: v(12),
      alignItems: 'center',
    },
    webForgotLinkText: {
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.primary,
      fontFamily: GTFonts.semibold,
      textDecorationLine: 'underline',
    },

    // ============ MOBILE STYLES ============
    safeArea: {
      backgroundColor: theme.colors.primaryDark,
      flex: 1,
    },
    scrollViewContent: {
      flex: 1,
    },
    scrollContainer: {
      backgroundColor: theme.colors.surface,
      flexGrow: 1,
    },
    maincontainer: {
      flex: 1,
      flexDirection: 'column',
    },
    top: {
      backgroundColor: theme.colors.primaryDark,
      justifyContent: 'flex-start',
      borderBottomLeftRadius: theme.borderRadius.xl,
      borderBottomRightRadius: theme.borderRadius.xl,
      paddingBottom: s(10),
    },

    imageContainer: {
      // paddingTop: s(15),
      justifyContent: 'center',
      alignItems: 'center',
    },
    topImage: {
      width: width * 0.8,
      height: height * 0.1,
      aspectRatio: 5,
      resizeMode: 'contain',
    },
    carouselContainer: {
      flex: 1,
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    carouselItemContainer: {
      flex: 1,
      width: width,
      alignItems: 'center',
      justifyContent: 'center',
    },
    carouselImage: {
      width: width * 0.96,
      height: '100%',
      borderRadius: theme.borderRadius.xl,
      opacity: 0.9,
    },
    logincontainer: {
      flex: 1,
      minHeight: height * 0.35,
      paddingTop: v(10),
      paddingHorizontal: s(8),
      borderTopLeftRadius: s(30),
      borderTopRightRadius: s(30),
      backgroundColor: theme.colors.surface,
    },
    loginContent: {
      flex: 1,
    },
    loginTitle: {
      marginTop: v(5),
      fontSize: fs(22),
      lineHeight: fs(24),
      fontFamily: GTFonts.semibold,
      color: theme.colors.shadow,
      marginBottom: v(20),
    },
    form: {
      marginBottom: v(10),
    },
    text: {
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
      marginBottom: v(10),
      left: 0,
      justifyContent: 'flex-start',
    },
    inputWrapper: {
      backgroundColor: theme.colors.surface,
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderColor: theme.colors.borderGray,
      height: v(50),
      borderRadius: theme.borderRadius.md,
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: v(10),
    },
    input: {
      flex: 1,
      color: 'black',
      fontSize: fs(16),
      fontFamily: GTFonts.regular,
      lineHeight: fs(18),
      height: '100%',
      paddingVertical: 0,
      paddingHorizontal: s(16),
    },
    eyes: {
      paddingHorizontal: s(8),
      paddingVertical: s(8),
    },
    firsticons: {
      paddingHorizontal: s(8),
      zIndex: 1000,
    },
    errorMessage: {
      marginTop: v(12),
      fontSize: fs(14),
      lineHeight: fs(18),
      color: 'red',
      fontFamily: GTFonts.regular,
      paddingHorizontal: s(40),
    },
    button: {
      elevation: 1,
      borderRadius: s(5),
      marginTop: v(20),
      marginHorizontal: s(30),
      marginVertical: v(5),
    },
    Scanbtn: {
      alignItems: 'center',
      borderRadius: s(5),
      height: v(38),
    },
    logintext: {
      fontSize: fs(20),
      lineHeight: fs(24),
      fontWeight: 'bold',
      color: 'white',
      alignSelf: 'center',
      marginRight: s(8),
      marginTop: v(5),
    },
    loader: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    buttonContainer: {
      gap: s(20),
    },
    formContainer: {},
    stickyButtonContainer: {
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(8),
      paddingVertical: s(20),
      gap: v(10),
      borderTopWidth: 1,
      borderTopColor: theme.colors.borderGray200,
    },
    loginLinkContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
    },
    loginText: {
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.semibold,
    },
    loginLink: {
      fontSize: fs(isTablet() ? 18 : 16),
      lineHeight: fs(isTablet() ? 22 : 20),
      color: theme.colors.primary,
      fontWeight: '600',
      textDecorationLine: 'underline',
      fontFamily: GTFonts.semibold,
    },
    forgotPasscodeRow: {
      alignItems: 'center',
      marginTop: v(16),
      marginBottom: v(4),
    },
    forgotPasscodeLink: {
      fontSize: fs(isTablet() ? 16 : 16),
      lineHeight: fs(isTablet() ? 20 : 18),
      color: theme.colors.primary,
      fontWeight: '600',
      textDecorationLine: 'underline',
      fontFamily: GTFonts.semibold,
    },
  });
};
